import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./onboarding.css";
import "react-step-progress-bar/styles.css";
import ProgressB from "../../components/ProgressBar";
import { useDynamic } from "../../hooks/DynamicProvider";

// Direct imports instead of lazy loading
import Access from "../../sections/Onboarding/Access";
import KYC from "../../sections/Onboarding/KYC";
import Funds from "../../sections/Onboarding/Funds";
import RXG from "../../sections/Onboarding/RXG";

const Onboarding = () => {
  const [direction, setDirection] = useState("forward");
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentQueryParams = location.search;
  const { step } = useParams();
  const { isLoading, userInfo, isLoggedIn } = useDynamic();

  /* useEffect(() => {
    const verifyUser = async () => {
      if (!isLoading) {
        if (!userMetadata) {
          navigate(`/onboarding/access${currentQueryParams}`);
          return;
        }

        try {
          if (!userInfo) {
            navigate(`/onboarding/access${currentQueryParams}`);
          }
        } catch (error) {
          console.error("Error verifying user:", error);
          navigate(`/onboarding/access${currentQueryParams}`);
        }
      }
    };

    verifyUser();
  }, [userMetadata, navigate, isLoading]); */

  useEffect(() => {
    if (!isLoading && !isLoggedIn && step !== "access")
      navigate(`/onboarding/access`);

    if (
      isLoggedIn &&
      userInfo &&
      (step === "funds" || step === "rxg") &&
      userInfo.is_verified != true
    )
      navigate(`/onboarding/access`);
  }, [step, isLoggedIn, userInfo, isLoading]);

  // Map step names to dynamic imports
  const sections = {
    access: Access,
    kyc: KYC,
    funds: Funds,
    rxg: RXG,
  };

  // Get the current step index and component based on the URL parameter
  const currentSectionName = step || "access";
  const CurrentSectionComponent =
    sections[currentSectionName] || sections.access;

  const nextStep = async () => {
    const sectionKeys = Object.keys(sections);
    const currentStepIndex = sectionKeys.indexOf(currentSectionName);
    if (currentStepIndex < sectionKeys.length - 1) {
      setDirection("forward");
      const nextSection = sectionKeys[currentStepIndex + 1];
      navigate(`/onboarding/${nextSection}${currentQueryParams}`);
    }
  };

  const prevStep = () => {
    const sectionKeys = Object.keys(sections);
    const currentStepIndex = sectionKeys.indexOf(currentSectionName);
    if (currentStepIndex > 0) {
      setDirection("backward");
      const prevSection = sectionKeys[currentStepIndex - 1];
      navigate(`/onboarding/${prevSection}${currentQueryParams}`);
    }
  };

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const paddingOffset = viewportHeight * 0.04;

        if (isMobile) {
          const newWidth = containerRef.current.offsetWidth * 0.96;
          const newHeight = viewportHeight - containerTop;
          setContainerSize({ width: newWidth, height: newHeight });
        } else {
          let newHeight;
          if (viewportHeight > 1750)
            newHeight = 1740 - containerTop - paddingOffset;
          else {
            newHeight =
              viewportHeight > viewportWidth / 1.2
                ? viewportWidth / 1.2 - containerTop - paddingOffset
                : viewportHeight - containerTop - paddingOffset;
            /* newHeight = viewportHeight - containerTop - paddingOffset; */
          }

          setContainerSize({ width: viewportWidth, height: newHeight });
        }
      }
    };

    const checkIfMobile = () => {
      const newIsMobile =
        window.innerWidth <= 768 ||
        window.innerWidth < window.innerHeight ||
        window.innerHeight < window.innerWidth / 2.7 ||
        window.innerHeight < 500;
      setIsMobile(newIsMobile);
    };

    checkIfMobile();
    updateContainerSize();

    window.addEventListener("resize", checkIfMobile);
    window.addEventListener("resize", updateContainerSize);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
      window.removeEventListener("resize", updateContainerSize);
    };
  }, [isMobile]);

  return (
    <div id="onboarding" className="onboarding">
      <div className="container-fluid">
        <div className="progressBarContainer">
          <ProgressB
            currentStep={Object.keys(sections).indexOf(currentSectionName)}
            totalSteps={Object.keys(sections).length}
          />
        </div>
        <div
          className={`onboarding-container ${isMobile ? "mobile" : ""} ${
            step === "kyc" ? "kyc-container" : ""
          }`}
          ref={containerRef}
          style={{ height: `${containerSize.height - 10}px` }}
        >
          <div
            className={`section-wrapper active ${
              direction === "forward" ? "slide-forward" : "slide-backward"
            }`}
          >
            {containerSize.height > 0 && (
              <CurrentSectionComponent
                onNext={nextStep}
                onPrev={prevStep}
                isActive={true}
                containerWidth={containerSize.width}
                containerHeight={containerSize.height - 20}
                isMobile={isMobile}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
